export interface StoryblokImageOptions {
    /** Target width in pixels. Omit (or 0) to keep aspect ratio based on height. */
    width?: number;
    /** Target height in pixels. Omit (or 0) to keep aspect ratio based on width. */
    height?: number;
    /** Smart cropping (focuses on faces) when both width and height are set. */
    smart?: boolean;
    /** Quality 0-100 for lossy formats. */
    quality?: number;
}

const STORYBLOK_ASSET_HOST = 'a.storyblok.com';

/**
 * Append a Storyblok image service transformation to an asset URL.
 * https://www.storyblok.com/docs/api/image-service
 *
 * Pass the displayed size x2 (for retina screens) as width and/or height.
 * Even without dimensions the bare `/m/` suffix is worth adding since it
 * enables automatic WebP/AVIF conversion based on the Accept header.
 *
 * Non-Storyblok URLs, SVGs and URLs that already contain a transformation
 * are returned unchanged, so it is always safe to wrap an asset filename.
 */
export function storyblokImage(
    filename: string | undefined | null,
    options: StoryblokImageOptions = {},
): string {
    if (!filename) {
        return '';
    }
    if (
        !filename.includes(STORYBLOK_ASSET_HOST) ||
        filename.includes('/m/') ||
        filename.toLowerCase().endsWith('.svg')
    ) {
        return filename;
    }

    const { width = 0, height = 0, smart, quality } = options;
    const segments = [];
    if (width || height) {
        segments.push(`${width}x${height}`);
    }
    if (smart && width && height) {
        segments.push('smart');
    }
    if (quality) {
        segments.push(`filters:quality(${quality})`);
    }
    // The bare format-optimization form is documented with a trailing slash: <asset>/m/
    return segments.length ? `${filename}/m/${segments.join('/')}` : `${filename}/m/`;
}

const IMG_SRC_REGEX = /(<img\b[^>]*\bsrc\s*=\s*)("([^"]*)"|'([^']*)')/gi;

/**
 * Apply storyblokImage to the src of every <img> tag in an HTML string,
 * e.g. rich text or markdown article content rendered to HTML.
 */
export function transformStoryblokImagesInHtml(
    html: string,
    options: StoryblokImageOptions = {},
): string {
    if (!html) {
        return html;
    }
    return html.replace(IMG_SRC_REGEX, (match, prefix, _quoted, doubleQuoted, singleQuoted) => {
        const src = doubleQuoted ?? singleQuoted ?? '';
        const transformed = storyblokImage(src, options);
        const quote = doubleQuoted !== undefined ? '"' : "'";
        return `${prefix}${quote}${transformed}${quote}`;
    });
}
